import React, { useCallback, useState } from "react";

import { InputNumber } from "primereact/inputnumber";
import ExpandableDataTable from "../../tables/ExpandableDataTable";
import { classNames, formatDateWithTime } from "../../../shared/utility";

/**
 * PrimeReact DataTable keeps expanded rows in an internal ref that syncs from
 * props in useEffect (after paint). A second row can be expanded before that ref
 * catches up, so onRowToggle may only contain the latest row. Merge with React
 * state for new expansions; when the incoming map is a strict subset of keys
 * (fewer rows expanded), treat it as a collapse and use it as-is.
 */
function mergeExpandedRowState(prev, next) {
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  const nextIsStrictCollapse =
    nextKeys.length < prevKeys.length &&
    nextKeys.every((k) => Object.prototype.hasOwnProperty.call(prev, k));
  if (nextIsStrictCollapse) {
    return next;
  }
  return { ...prev, ...next };
}

export default function OrderItemsForShipping(props) {
  const {
    askDeleteShippingItem,
    orderItems,
    order_id,
    onExpandRow,
    setProductTemperature,
    onProductTemperatureUpdated,
    user_type,
    onPrintShippingLabel,
  } = props;

  const [expandedRows, setExpandedRows] = useState({});
  const [printingRowId, setPrintingRowId] = useState(null);

  const showShippingLabelButton =
    parseInt(String(user_type ?? 0), 10) !== 0 && onPrintShippingLabel;

  const handleExpandedRowsChange = useCallback((nextExpanded) => {
    setExpandedRows((prev) => mergeExpandedRowState(prev, nextExpanded ?? {}));
  }, []);

  const allowExpansion = (rowData) => {
    const shippingItems = rowData.shippingItems ? rowData.shippingItems : [];
    // return shippingItems.length > 0;
    return true;
  };

  const shippingItemActionTemplate = (rowData, row) => {
    return (
      <div className="ml-10">
        <button
          onClick={() => askDeleteShippingItem(rowData)}
          className="ml-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
        >
          Delete
        </button>
      </div>
    );
  };

  const lotNumberTemplate = (rowData) => {
    return <p>{rowData.lot_number}</p>;
  };

  const packagingDateTemplate = (rowData) => {
    return <p>{formatDateWithTime(rowData.packaging_date)}</p>;
  };

  const packagingSizeTemplate = (rowData) => {
    return <p>{rowData.packing_size} kg</p>;
  };

  const barcodeBody = (rowData) => {
    let fontColor = null;
    return (
      <p className={classNames(fontColor, "font-medium")}>{rowData.barcode}</p>
    );
  };

  const productDetailsBody = (rowData) => {
    return (
      <p>
        <span className="text-gray-900 font-semibold">
          {rowData.product_name}
        </span>
        <br />
        <span className="text-gray-700">PLU:{rowData.plu}</span>
      </p>
    );
  };

  const productTemperatureBody = (rowData) => {
    if (rowData.product_temperature === null) {
      return <p> </p>;
    }
    return <p>{rowData.product_temperature} </p>;
  };

  const temperatureEditor = (options) => {
    return (
      <InputNumber
        value={options.value ?? null}
        onValueChange={(e) => options.editorCallback(e.value)}
        onKeyDown={(e) => e.stopPropagation()}
        minFractionDigits={0}
        maxFractionDigits={1}
        placeholder="°C"
      />
    );
  };

  const handleShippingLabelClick = async (rowData) => {
    if (!onPrintShippingLabel) return;
    setPrintingRowId(rowData.id);
    try {
      await onPrintShippingLabel(rowData);
    } finally {
      setPrintingRowId(null);
    }
  };

  const shippingLabelBody = (rowData) => (
    <button
      type="button"
      disabled={printingRowId === rowData.id}
      onClick={() => handleShippingLabelClick(rowData)}
      className="rounded-md bg-main-purple px-2 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
    >
      Shipping Label
    </button>
  );

  const onRowEditComplete = async (e) => {
    const { newData, index } = e;
    const temperature = newData.product_temperature;

    const result = await setProductTemperature(
      newData.product_id,
      temperature,
      order_id,
    );

    if (result?.success) {
      onProductTemperatureUpdated(index, temperature);
    }
  };

  const orderQuantityBody = (rowData) => {
    return (
      <p>
        {rowData.quantity} {rowData.selling_unit_of_measure}
      </p>
    );
  };

  const shippedQuantity = (rowData) => {
    return (
      <p>
        {rowData.shipped_amount} {rowData.selling_unit_of_measure}
      </p>
    );
  };

  const shippingItemQuantityBody = (rowData) => {
    return <p>{rowData.carton_count}</p>;
  };

  const shippingItemColumns = [
    { header: "Barcode", body: barcodeBody },
    { header: "Carton Count", body: shippingItemQuantityBody },
    { header: "Packing Size", body: packagingSizeTemplate },
    { header: "Lot Number", body: lotNumberTemplate },
    { header: "Packaged date", body: packagingDateTemplate },
    { header: "", body: shippingItemActionTemplate },
  ];

  const renderExpandedOrderItemRow = (orderItem) => {
    const shippingItems = orderItem?.shippingItems ?? [];
    return (
      <div className="p-3">
        <ExpandableDataTable
          data={shippingItems}
          dataKey="id"
          columns={shippingItemColumns}
          tableClassName="min-w-full bg-white divide-y divide-gray-300"
          cellClassName="py-2 bg-gray-100 border-1-bottom-gray px-2 text-sm"
        />
      </div>
    );
  };

  const productColWidth = showShippingLabelButton ? "30%" : "35%";
  const standardColWidth = showShippingLabelButton ? "18%" : "20%";

  const orderItemColumns = [
    { header: "Product", body: productDetailsBody, width: productColWidth },
    {
      header: "Order quantity/Cs",
      body: orderQuantityBody,
      width: standardColWidth,
    },
    {
      header: "Shipped quantity",
      body: shippedQuantity,
      width: standardColWidth,
    },
    {
      field: "product_temperature",
      header: "Product Temperature",
      body: productTemperatureBody,
      editable: true,
      editor: (options) => temperatureEditor(options),
      width: standardColWidth,
    },
    ...(showShippingLabelButton
      ? [
          {
            header: "Shipping Label",
            body: shippingLabelBody,
            width: "16%",
          },
        ]
      : []),
  ];

  return (
    <div className="mt-5 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-4 sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden">
            <ExpandableDataTable
              data={orderItems}
              dataKey="id"
              columns={orderItemColumns}
              expandable
              allowExpansion={allowExpansion}
              renderExpandedRow={renderExpandedOrderItemRow}
              expandedRows={expandedRows}
              onExpandedRowsChange={handleExpandedRowsChange}
              onRowExpand={onExpandRow}
              editable
              editMode="row"
              onRowEditComplete={onRowEditComplete}
              tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
              cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
